import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trophy, ExternalLink, Bell, Shield, Calendar, Info, Star } from "lucide-react";
import { KHL_TEAMS } from "@/data/khlTeams";
import { Link } from "react-router-dom";

// Projected standings based on late-season data (March 2026)
const WEST_STANDINGS = [
  { seed: 1, team: "Lokomotiv Yaroslavl", abbr: "LOK", pts: 91, gp: 62, w: 38, l: 24 },
  { seed: 2, team: "SKA Saint Petersburg", abbr: "SKA", pts: 88, gp: 62, w: 37, l: 25 },
  { seed: 3, team: "CSKA Moscow", abbr: "CSKA", pts: 85, gp: 62, w: 35, l: 27 },
  { seed: 4, team: "Dynamo Moscow", abbr: "DYN", pts: 82, gp: 62, w: 34, l: 28 },
  { seed: 5, team: "Spartak Moscow", abbr: "SPR", pts: 78, gp: 62, w: 32, l: 30 },
  { seed: 6, team: "Torpedo Nizhny Novgorod", abbr: "TOR", pts: 74, gp: 62, w: 30, l: 32 },
  { seed: 7, team: "Severstal Cherepovets", abbr: "SEV", pts: 71, gp: 62, w: 29, l: 33 },
  { seed: 8, team: "Dinamo Minsk", abbr: "DMN", pts: 68, gp: 62, w: 27, l: 35 },
];

const EAST_STANDINGS = [
  { seed: 1, team: "Metallurg Magnitogorsk", abbr: "MMG", pts: 97, gp: 62, w: 41, l: 21 },
  { seed: 2, team: "Ak Bars Kazan", abbr: "AKB", pts: 90, gp: 62, w: 38, l: 24 },
  { seed: 3, team: "Avtomobilist Yekaterinburg", abbr: "AVT", pts: 86, gp: 62, w: 36, l: 26 },
  { seed: 4, team: "Traktor Chelyabinsk", abbr: "TRK", pts: 83, gp: 62, w: 34, l: 28 },
  { seed: 5, team: "Salavat Yulaev Ufa", abbr: "SAL", pts: 80, gp: 62, w: 33, l: 29 },
  { seed: 6, team: "Avangard Omsk", abbr: "AVG", pts: 76, gp: 62, w: 31, l: 31 },
  { seed: 7, team: "Sibir Novosibirsk", abbr: "SIB", pts: 72, gp: 62, w: 29, l: 33 },
  { seed: 8, team: "Barys Astana", abbr: "BAR", pts: 69, gp: 62, w: 28, l: 34 },
];

const getTeamLogo = (abbr: string) => {
  const team = KHL_TEAMS.find((t) => t.abbr === abbr);
  return team?.logo || "";
};

const ConferenceTable = ({
  title,
  conference,
  standings,
}: {
  title: string;
  conference: "Western" | "Eastern";
  standings: typeof WEST_STANDINGS;
}) => (
  <Card className="border-blue-900/40 bg-gradient-to-b from-blue-950/60 to-card">
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2 text-lg">
        <Shield className="h-5 w-5 text-blue-400" />
        {title}
      </CardTitle>
      <CardDescription>Top 8 qualify for Gagarin Cup Playoffs</CardDescription>
    </CardHeader>
    <CardContent className="p-0">
      <Table>
        <TableHeader>
          <TableRow className="border-blue-900/30 hover:bg-transparent">
            <TableHead className="w-10 text-center">#</TableHead>
            <TableHead>Team</TableHead>
            <TableHead className="text-center">GP</TableHead>
            <TableHead className="text-center">W</TableHead>
            <TableHead className="text-center">L</TableHead>
            <TableHead className="text-center font-bold">PTS</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {standings.map((row) => (
            <TableRow
              key={row.abbr}
              className="border-blue-900/20 hover:bg-blue-950/40"
            >
              <TableCell className="text-center font-mono text-muted-foreground">
                {row.seed <= 3 ? (
                  <span className="flex items-center justify-center">
                    <Trophy className={`h-3.5 w-3.5 ${row.seed === 1 ? "text-yellow-400" : row.seed === 2 ? "text-slate-300" : "text-amber-600"}`} />
                  </span>
                ) : (
                  row.seed
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {getTeamLogo(row.abbr) && (
                    <img
                      src={getTeamLogo(row.abbr)}
                      alt={row.team}
                      className="h-6 w-6 object-contain"
                      loading="lazy"
                    />
                  )}
                  <span className="font-medium text-sm">{row.team}</span>
                </div>
              </TableCell>
              <TableCell className="text-center text-muted-foreground">{row.gp}</TableCell>
              <TableCell className="text-center">{row.w}</TableCell>
              <TableCell className="text-center text-muted-foreground">{row.l}</TableCell>
              <TableCell className="text-center font-bold text-blue-300">{row.pts}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

const KHLPlayoffs = () => {
  return (
    <div className="min-h-screen bg-background pt-16">
      <Navbar />
      <main className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Hero Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 mb-3">
            <Trophy className="h-8 w-8 text-yellow-400" />
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
              Gagarin Cup Playoffs 2025/26
            </h1>
            <Trophy className="h-8 w-8 text-yellow-400" />
          </div>
          <p className="text-muted-foreground text-lg">
            Starts March 23, 2026 · Best-of-7 series · Cross-conference re-seeding after quarters
          </p>
        </div>

        {/* Key Info Banner */}
        <Card className="mb-8 border-blue-800/50 bg-gradient-to-r from-blue-950/80 via-blue-900/40 to-blue-950/80">
          <CardContent className="py-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div className="flex flex-col items-center gap-1">
                <Shield className="h-6 w-6 text-blue-400" />
                <span className="text-sm font-medium">Top 8 per conference</span>
                <span className="text-xs text-muted-foreground">advance to playoffs</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <Star className="h-6 w-6 text-yellow-400" />
                <span className="text-sm font-medium">Seeded 1–8</span>
                <span className="text-xs text-muted-foreground">within each conference</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <Trophy className="h-6 w-6 text-amber-400" />
                <span className="text-sm font-medium">Finals → Gagarin Cup</span>
                <span className="text-xs text-muted-foreground">champion crowned</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Conference Standings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <ConferenceTable title="Western Conference" conference="Western" standings={WEST_STANDINGS} />
          <ConferenceTable title="Eastern Conference" conference="Eastern" standings={EAST_STANDINGS} />
        </div>

        {/* Bracket Placeholder */}
        <Card className="mb-8 border-dashed border-blue-800/60 bg-blue-950/30">
          <CardContent className="py-10 text-center">
            <Calendar className="h-10 w-10 text-blue-400 mx-auto mb-3" />
            <h2 className="text-xl font-semibold mb-2">Playoff Bracket Coming Soon</h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              The full playoff bracket will appear after the regular season ends (mid-March&nbsp;2026).
              Format: Best-of-7 series with cross-conference re-seeding after the quarterfinals.
            </p>
          </CardContent>
        </Card>

        {/* News Snippet */}
        <Card className="mb-8 border-blue-900/40">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Info className="h-5 w-5 text-blue-400" />
              Latest News
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <Badge variant="secondary" className="shrink-0 mt-0.5">Mar&nbsp;2026</Badge>
                <span>SKA St.&nbsp;Petersburg clinched a playoff spot in early March. Regular season still ongoing.</span>
              </li>
              <li className="flex items-start gap-2">
                <Badge variant="secondary" className="shrink-0 mt-0.5">Projected</Badge>
                <span>Metallurg Magnitogorsk leads the East with 97 pts; Lokomotiv tops the West with 91 pts.</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Pro/Elite CTA + External Link */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Card className="border-yellow-800/40 bg-gradient-to-br from-yellow-950/40 to-card">
            <CardContent className="py-6 flex flex-col items-center text-center gap-3">
              <Bell className="h-7 w-7 text-yellow-400" />
              <p className="font-medium">Get playoff alerts &amp; bracket updates</p>
              <Link to="/pricing">
                <Button variant="outline" className="border-yellow-700/60 hover:bg-yellow-900/30">
                  Subscribe Now
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="border-blue-900/40">
            <CardContent className="py-6 flex flex-col items-center text-center gap-3">
              <ExternalLink className="h-7 w-7 text-blue-400" />
              <p className="font-medium">Official KHL live updates &amp; stats</p>
              <a href="https://en.khl.ru/" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-blue-700/60 hover:bg-blue-900/30">
                  Visit en.khl.ru <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                </Button>
              </a>
            </CardContent>
          </Card>
        </div>

        {/* Timestamp */}
        <p className="text-center text-xs text-muted-foreground">
          Last updated: March 2026 · Standings are projected and may change before playoffs begin.
        </p>
      </main>
      <Footer />
    </div>
  );
};

export default KHLPlayoffs;
